"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";

import worldMapImg from "@/../public/image/new-version/home/world_map.png";

const SLUG_CONFIG: Record<string, { namespace: string; translationKeyPrefix: string }> = {
	stamping: { namespace: "new-stamping", translationKeyPrefix: "globalNetwork" },
	welding: { namespace: "new-welding", translationKeyPrefix: "globalNetwork" },
	"metal-casting": { namespace: "new-metal-casting", translationKeyPrefix: "globalNetwork" },
	"sand-casting": { namespace: "new-sand-casting", translationKeyPrefix: "globalNetwork" },
	"die-casting": { namespace: "new-die-casting", translationKeyPrefix: "globalNetwork" },
	"3d-printing": { namespace: "new-3d-printing", translationKeyPrefix: "globalNetwork" },
	"aluminum-extrusion": { namespace: "new-aluminum-extrusion", translationKeyPrefix: "globalNetwork" },
	"surface-finishing": { namespace: "new-surface-finishing", translationKeyPrefix: "globalNetwork" },
	"on-demand-manufacturing": { namespace: "new-on-demand-manufacturing", translationKeyPrefix: "globalNetwork" },
	assembly: { namespace: "new-assembly", translationKeyPrefix: "globalNetwork" },
	"industrial-automation": { namespace: "new-industrial-automation", translationKeyPrefix: "globalNetwork" },
};

const FALLBACK = { namespace: "metalStampingWorkshop", translationKeyPrefix: "" as const };

const STATS = [
	{ valueKey: "stat1Value", labelKey: "stat1Label" },
	{ valueKey: "stat2Value", labelKey: "stat2Label" },
	{ valueKey: "stat3Value", labelKey: "stat3Label" },
	{ valueKey: "stat4Value", labelKey: "stat4Label" },
] as const;

function translationKey(prefix: string, base: string) {
	if (!prefix) return base;
	return `${prefix}${base.charAt(0).toUpperCase()}${base.slice(1)}`;
}

export default function GlobalNetwork({ slug }: { slug: string }) {
	const { namespace, translationKeyPrefix } = SLUG_CONFIG[slug] ?? FALLBACK;
	const t = useTranslations(namespace);
	const tk = (base: string) => translationKey(translationKeyPrefix, base);
	const mapImageAlt = translationKeyPrefix ? t(tk("mapImageAlt")) : "World Map Background";
	const stats = STATS.filter((stat) => t.has(tk(stat.valueKey)) && t.has(tk(stat.labelKey)));

	return (
		<div className="sm:pt-10 md:pt-12 lg:pt-16">
			<section
				className="relative isolate overflow-hidden bg-[#ffffff] py-8 forced-color-adjust-none [color-scheme:light] sm:py-10 md:py-12 lg:py-16"
				style={{ colorScheme: "light" }}
			>
				<div className="pointer-events-none absolute inset-0 z-0 bg-[#ffffff]">
					<div className="container relative h-full min-h-[200px]">
						<Image
							src={worldMapImg}
							alt={mapImageAlt}
							fill
							className="object-contain object-center opacity-60"
							sizes="(max-width: 640px) 100vw, (max-width: 768px) 640px, (max-width: 1024px) 768px, (max-width: 1280px) 1024px, 1280px"
						/>
					</div>
				</div>

				<div className="container relative z-10">
					<div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-12 xl:gap-16">
						<div className="lg:max-w-[40%] lg:shrink-0 xl:max-w-[42%]">
							<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
								<span className="block leading-[1.12]">{t(tk("titleLine1"))}</span>
								<span className="mt-0.5 flex items-end gap-2.5 leading-[1.12] sm:gap-3">
									<span>{t(tk("titleLine2"))}</span>
								</span>
							</h2>

							<div className="space-y-4 text-sm leading-relaxed text-black md:text-base">
								<p>{t(tk("description1"))}</p>
								<p>{t(tk("description2"))}</p>
							</div>
						</div>

						<div className="flex min-w-0 flex-1 flex-wrap justify-start gap-6 sm:gap-8 lg:flex-wrap lg:justify-between lg:gap-8 xl:gap-12">
							{stats.map((stat, index) => (
								<div key={index} className="text-center lg:text-left">
									<div className="mb-6 text-2xl font-bold text-themePrimary400 sm:text-3xl md:text-4xl lg:text-[42px]">
										{t(tk(stat.valueKey))}
									</div>
									<div className="whitespace-nowrap text-sm text-black md:text-base">
										{t(tk(stat.labelKey))}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>
		</div>
	);
}
